import React, { useRef, useCallback, useEffect } from "react";
import { Box } from "@mui/material";
import {
  EditorContainer,
  Toolbar,
  ToolbarButton,
  EditorContent,
  Label,
} from "./RichTextEditor.styled";

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  minHeight?: number;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = "",
  label,
  disabled = false,
  minHeight = 100,
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const isInternalChange = useRef(false);

  // Update editor content when value changes from outside
  useEffect(() => {
    if (editorRef.current && !isInternalChange.current) {
      if (editorRef.current.innerHTML !== value) {
        editorRef.current.innerHTML = value;
      }
    }
    isInternalChange.current = false;
  }, [value]);

  const handleInput = useCallback(() => {
    if (editorRef.current) {
      isInternalChange.current = true;
      onChange(editorRef.current.innerHTML);
    }
  }, [onChange]);

  const execCommand = useCallback(
    (command: string, value?: string) => {
      if (disabled) return;

      // Focus the editor before executing command
      editorRef.current?.focus();
      document.execCommand(command, false, value);
      handleInput();
    },
    [disabled, handleInput],
  );

  const handleBold = useCallback(() => {
    execCommand("bold");
  }, [execCommand]);

  const handleItalic = useCallback(() => {
    execCommand("italic");
  }, [execCommand]);

  const handleUnderline = useCallback(() => {
    execCommand("underline");
  }, [execCommand]);

  const isFormatActive = useCallback((format: string): boolean => {
    return document.queryCommandState(format);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // Support Ctrl+B for bold
      if (e.ctrlKey || e.metaKey) {
        if (e.key === "b") {
          e.preventDefault();
          handleBold();
        } else if (e.key === "i") {
          e.preventDefault();
          handleItalic();
        } else if (e.key === "u") {
          e.preventDefault();
          handleUnderline();
        }
      }
    },
    [handleBold, handleItalic, handleUnderline],
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      // Get HTML from clipboard if available, otherwise get plain text
      const html = e.clipboardData.getData("text/html");
      const text = e.clipboardData.getData("text/plain");

      if (html) {
        e.preventDefault();
        // Clean up HTML - allow only basic formatting tags
        const cleanHtml = html
          .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "")
          .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, "")
          .replace(/class="[^"]*"/gi, "")
          .replace(/style="[^"]*"/gi, "")
          .replace(/id="[^"]*"/gi, "");

        document.execCommand("insertHTML", false, cleanHtml);
        handleInput();
      } else if (text) {
        // Plain text - convert line breaks to <br>
        e.preventDefault();
        const formattedText = text.replace(/\n/g, "<br>");
        document.execCommand("insertHTML", false, formattedText);
        handleInput();
      }
    },
    [handleInput],
  );

  return (
    <Box>
      {label && <Label>{label}</Label>}
      <EditorContainer>
        <Toolbar>
          <ToolbarButton
            type="button"
            onClick={handleBold}
            isActive={isFormatActive("bold")}
            disabled={disabled}
            title="Bold (Ctrl+B)"
          >
            <strong>B</strong>
          </ToolbarButton>
          <ToolbarButton
            type="button"
            onClick={handleItalic}
            isActive={isFormatActive("italic")}
            disabled={disabled}
            title="Italic (Ctrl+I)"
          >
            <em>I</em>
          </ToolbarButton>
          <ToolbarButton
            type="button"
            onClick={handleUnderline}
            isActive={isFormatActive("underline")}
            disabled={disabled}
            title="Underline (Ctrl+U)"
          >
            <u>U</u>
          </ToolbarButton>
        </Toolbar>
        <EditorContent
          ref={editorRef}
          contentEditable={!disabled}
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          data-placeholder={placeholder}
          style={{ minHeight }}
          suppressContentEditableWarning
        />
      </EditorContainer>
    </Box>
  );
};

export default RichTextEditor;
